import { AsyncPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Injector,
  input,
  InputSignal,
  OnChanges,
  output,
  OutputEmitterRef,
  RESPONSE_INIT,
  ResponseInit,
  signal,
  Signal,
  viewChildren,
  ViewContainerRef,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContentConfig, PageBlockModel, PageModel } from '@libs/cms/models';
import { MaintenanceComponent, TextLabelComponent } from '@libs/common/components';
import { CMS_PAGE_ROUTE_CONTEXT, CmsPageRouteContext } from '@libs/services/components';
import { CapabilityService } from '@libs/shared/services';
import { CMS_CONSENT_ENABLED, CmsService, ConsentManagerService } from '@libs/shared/services';
import { firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { ContentBuilderService } from './services';
import { ContentRemoteDataResolverService } from './services/content-builder/content-remote-data-resolver.service';

@Component({
  selector: 'ui-cms',
  templateUrl: 'cms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ContentBuilderService,
    ContentRemoteDataResolverService,
  ],
  imports: [
    AsyncPipe,
    TextLabelComponent,
    MaintenanceComponent,
  ],
})
export class CmsComponent implements OnChanges, AfterViewInit {
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly cmsService: CmsService = inject(CmsService);
  private readonly consentManager: ConsentManagerService = inject(ConsentManagerService);
  private readonly contentBuilder: ContentBuilderService = inject(ContentBuilderService);
  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly responseInit: ResponseInit | null = inject(RESPONSE_INIT, { optional: true });
  private requestVersion: number = 0;

  public hasContent: OutputEmitterRef<boolean> = output();
  public pageLoaded: OutputEmitterRef<PageModel> = output();

  public pageBlocksRefs: Signal<readonly ViewContainerRef[]> = viewChildren('pageBlock', { read: ViewContainerRef });
  public showErrorFallback: WritableSignal<boolean> = signal(false);
  public errorFallbackEnabled: InputSignal<boolean> = input(false);
  public notFoundPageEnabled: InputSignal<boolean> = input(false);
  public consentEnabled: InputSignal<boolean> = input(inject(CMS_CONSENT_ENABLED, { optional: true }) ?? false);

  public config: InputSignal<ContentConfig> = input.required();

  public page$: ReplaySubject<PageModel | undefined> = new ReplaySubject(1);

  public ngOnChanges(): void {
    const requestVersion: number = ++this.requestVersion;

    this.page$.next(undefined);
    this.showErrorFallback.set(false);

    const config: ContentConfig = this.config();
    const isConfigMode: boolean = this.capabilityService.isAvailable('platform.cmsAppConfigMode')();
    const contentId: string | number = config.contentId ?? '';
    const contentPath: string = config.path ?? '';

    if (isConfigMode ? !contentPath : !contentId) {
      return;
    }

    const result$: Observable<PageModel> = isConfigMode
      ? this.cmsService.getAppContent(contentPath)
      : this.cmsService.getPage(contentId, config.customerNumber);

    firstValueFrom(result$)
      .then((page: PageModel) => {
        if (requestVersion === this.requestVersion) {
          this.handlePage(page);
        }
      })
      .catch((error: unknown) => {
        if (requestVersion === this.requestVersion) {
          void this.handlePageError(error);
        }
      });
  }

  public ngAfterViewInit(): void {
    this.page$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.updateContent.bind(this));
  }

  private handlePage(page: PageModel): void {
    if (this.consentEnabled()) {
      this.consentManager.initializeForPage(page.hideConsentBanner);
    }

    this.pageLoaded.emit(page);
    void this.page$.next(page);
  }

  private handlePageError(error: unknown): void {
    const status: unknown = (error as { status?: unknown })?.status;

    if (typeof status === 'number' && status >= 400 && status < 500) {
      this.handleClientError(status, error);

      return;
    }

    if (this.errorFallbackEnabled()) {
      this.showErrorFallback.set(true);
      this.hasContent.emit(true);
    } else {
      this.hasContent.emit(false);
    }
  }

  private handleClientError(status: number, error: unknown): void {
    if (status === 404 && this.notFoundPageEnabled()) {
      if (this.responseInit) {
        this.responseInit.status = 404;
      }

      const page: unknown = (error as { data?: unknown })?.data;

      if (this.isPageResponse(page)) {
        this.handlePage(page);

        return;
      }
    }

    this.hasContent.emit(false);
  }

  /**
   * Distinguishes a CMS page from a regular error payload by its blocks array.
   * Relies on the BFF contract for the remaining page structure.
   */
  private isPageResponse(page: unknown): page is PageModel {
    return Array.isArray((page as Partial<PageModel> | null)?.blocks);
  }

  private updateContent(page?: PageModel): void {
    // Refresh view layout (updates this.pageBlockRefs)
    this.changeDetector.detectChanges();

    // Insert content
    let hasContent: boolean = false;
    const routeContext: CmsPageRouteContext | undefined = this.createRouteContext(page);

    this.pageBlocksRefs().forEach((blockViewRef: ViewContainerRef, blockViewIndex: number) => {
      blockViewRef.clear();

      const block: PageBlockModel | undefined = page?.blocks.find(
        (item: PageBlockModel) => item.id === blockViewIndex,
      );

      if (!block) {
        return;
      }

      const contentInjector: Injector | undefined = routeContext
        ? this.createContentInjector(routeContext, blockViewRef.injector)
        : undefined;

      this.contentBuilder.build(block.content, blockViewRef, contentInjector);

      hasContent = hasContent || Boolean(block.content.length);
    });

    // Render content
    this.changeDetector.detectChanges();

    // Emit has content
    this.hasContent.emit(hasContent);
  }

  private createRouteContext(page: PageModel | undefined): CmsPageRouteContext | undefined {
    if (!page || !this.capabilityService.isAvailable('platform.cmsAppConfigMode')()) {
      return undefined;
    }

    return {
      basePath: page.url,
      urlSuffix: page.urlSuffix,
    };
  }

  private createContentInjector(routeContext: CmsPageRouteContext, parent: Injector): Injector {
    return Injector.create({
      providers: [
        {
          provide: CMS_PAGE_ROUTE_CONTEXT,
          useValue: routeContext,
        },
      ],
      parent,
    });
  }
}
